/**
 * Ported from `packages/ja-core/src/schemas.ts` in `git@git.fugl.dev:sks/yominoki.git`
 * @ `515ce239`. Bindery is canonical for this module from 2026-09-06; reconcile yominoki-side
 * drift toward this copy.
 *
 * Three things changed on the way in.
 *
 * **The fluency and treatment vocabularies come from `@bindery/shared`.** yominoki declared them
 * here as TS `enum`s and mirrored them with `z.nativeEnum`; `erasableSyntaxOnly` forbids enums
 * repo-wide, and the enums themselves are already canonical in `@bindery/shared/contracts/*` as
 * `as const` objects. zod 4's `z.enum()` accepts an enum-like object directly, so the mirrors
 * below are the same five schemas over the same five value sets — and each one is annotated
 * `z.ZodType<T>` against the shared union so a drift in either direction is a compile error
 * rather than a schema that quietly admits a value the type rejects.
 *
 * **`zLearningPosition` is pinned to `@bindery/shared`'s `LearningPosition`.** It is a genuine
 * runtime mirror of a shared type — the discriminated union `apps` validate a posted engagement
 * against — so the annotation is the whole point of keeping it here rather than deleting it.
 *
 * **`LearningRecordContext` and `LearningRecordEntry` are deliberately NOT shared's.** Shared's
 * `LearningRecordContext` spells the same two facts differently (`word?`/`readerId` where this
 * says `surface`/`userId`) and carries a `wordInReading` this does not; shared declined to port
 * `LearningRecordEntry` at all. What these two schemas describe is the shape already sitting in
 * yominoki's CockroachDB — the `learning_records.context` jsonb column and the entry
 * `packages/yominoki-db`'s `insertLearningRecord` takes — so they stay spelled the way the rows
 * are spelled. Reconciling the names is a data migration, not a rename; see
 * `@bindery/shared/contracts/learning`'s header for the table of differences.
 */

// Zod schemas for all domain types + DB row shapes.
// Row schemas are snake_case and mirror the SQL DDL in ../../yominoki-db/migrations.
// Any drift between this file and that DDL is a bug — schemas here are the
// source of truth.

import type {
  ActionId,
  ResponseId,
  WordReadingFluencyEnum,
} from "@bindery/shared/contracts/fluency";
import {
  ActionId as ActionIdValues,
  ResponseId as ResponseIdValues,
  WordReadingFluencyEnum as WordReadingFluencyEnumValues,
} from "@bindery/shared/contracts/fluency";
import type {
  LearningPosition,
  Treatment,
  TreatmentId,
} from "@bindery/shared/contracts/learning";
import {
  Treatment as TreatmentValues,
  TreatmentId as TreatmentIdValues,
} from "@bindery/shared/contracts/learning";
// `import * as z`, not `import { z }`. zod v4's entry does re-export a `z`
// binding, but Vite's SSR transform does not see it through the package's
// export conditions, so under vitest the named form is `undefined` and every
// schema below throws at module init. Nothing here had a test until
// apps/yominoki imported it, which is why the whole workspace's other 13
// zod importers already use the namespace form and this one did not.
import * as z from "zod";

// ---------- enums ----------

export const zResponseId: z.ZodType<ResponseId> = z.enum(ResponseIdValues);
export const zActionId: z.ZodType<ActionId> = z.enum(ActionIdValues);
export const zFluencyLevel: z.ZodType<WordReadingFluencyEnum> = z.enum(
  WordReadingFluencyEnumValues,
);
export const zTreatment: z.ZodType<Treatment> = z.enum(TreatmentValues);
export const zTreatmentId: z.ZodType<TreatmentId> = z.enum(TreatmentIdValues);

// ---------- domain objects ----------

export const zLearningPosition: z.ZodType<LearningPosition> =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("reading-token"),
      readingId: z.uuid(),
      sentenceId: z.uuid(),
      tokenIdx: z.number().int().nonnegative(),
    }),
    z.object({
      kind: z.literal("batch-tile"),
      jmdictSeq: z.string().min(1),
      rankInBatch: z.number().int().nonnegative(),
      pos: z.string().min(1),
    }),
    z.object({
      kind: z.literal("drill-sentence"),
      sentenceId: z.uuid(),
      readingId: z.uuid(),
      direction: z.enum(["ja_to_en", "en_to_ja"]),
    }),
  ]);

export const zLearningRecordContext = z.object({
  surface: z.string().min(1),
  response: zResponseId,
  prediction: z
    .object({
      responseId: zResponseId,
      confidence: z.number().min(0).max(1).optional(),
    })
    .optional(),
  actionId: zActionId,
  treatmentId: zTreatmentId,
  userId: z.uuid(),
  sessionId: z.uuid(),
  sessionStartTime: z.number().optional(),
  createdAt: z.number().int(),
  pageStartTime: z.number().optional(),
  readingRate: z.number().optional(),
  targetFluencyRecord: z.array(zResponseId).optional(),
  precedingFluencyRecord: z.array(zResponseId).optional(),
  choicesPresented: z.array(z.string()).optional(),
  incorrectMatches: z.array(z.string()).optional(),
  utcOffset: z.number().optional(),
  position: zLearningPosition.optional(),
});

export const zLearningRecordEntry = z.object({
  createdAt: z.date(),
  surface: z.string().min(1),
  prediction: zResponseId.optional(),
  response: zResponseId,
  actionId: zActionId,
  treatmentId: zTreatmentId,
  context: zLearningRecordContext,
});

// ---------- DB row schemas (snake_case) ----------

const uuid = z.uuid();
const ts = z.iso.datetime({ offset: true });
const bytes = z.instanceof(Uint8Array);

export const zUserRow = z.object({
  id: uuid,
  did: z.string().min(1),
  handle: z.string().nullable().optional(),
  display_name: z.string().nullable().optional(),
  avatar_url: z.url().nullable().optional(),
  created_at: ts,
  updated_at: ts,
  stripe_customer_id: z.string().nullable().optional(),
  plan: z.string().default("free"),
  plan_status: z.string().default("active"),
  vectorize_my_posts: z.boolean().default(false),
});

export const zUserSelfCentroidRow = z.object({
  user_id: uuid,
  centroid: z.array(z.number()).length(1024),
  post_count: z.number().int().nonnegative(),
  updated_at: ts,
});

export const zUsageEventKind = z.enum([
  "gloss.miss",
  "gloss.hit",
  "tts.miss",
  "tts.hit",
  "embed.miss",
  "embed.hit",
  "etymology.miss",
  "etymology.hit",
  "llm.other",
]);

export const zUsageEventRow = z.object({
  id: uuid,
  user_id: uuid,
  kind: zUsageEventKind,
  scope: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  input_tokens: z.number().int().nullable().optional(),
  output_tokens: z.number().int().nullable().optional(),
  audio_ms: z.number().int().nullable().optional(),
  text_hash: bytes.nullable().optional(),
  reading_id: uuid.nullable().optional(),
  created_at: ts,
});

// ---------- morphology ----------

export const zVerbKind = z.enum(["consonant", "vowel", "irregular"]);
export const zAdjectiveKind = z.enum(["い", "な"]);

export const zVerbSplit = z.object({
  stem: z.string(),
  ending: z.string(),
  kind: zVerbKind,
  romaji: z.object({ stem: z.string(), ending: z.string() }).optional(),
});

export const zAdjectiveSplit = z.object({
  stem: z.string(),
  ending: z.string(),
  kind: zAdjectiveKind,
});

export const zReadingSource = z.enum(["bluesky", "distinbox"]);

export const zReadingRow = z.object({
  id: uuid,
  source: zReadingSource,
  source_ref: z.string(),
  source_uri: z.url().nullable().optional(),
  source_cid: z.string().nullable().optional(),
  author_did: z.string().nullable().optional(),
  lang: z.string(),
  text: z.string().min(1),
  created_at: ts,
  ingested_at: ts,
  sentence_count: z.number().int().nonnegative(),
  token_count: z.number().int().nonnegative(),
  approx_tiktoken_count: z.number().int().nonnegative(),
  embedding: z.array(z.number()).length(1024).nullable().optional(),
});

export const zSentenceRow = z.object({
  id: uuid,
  reading_id: uuid,
  idx: z.number().int().nonnegative(),
  text: z.string().min(1),
  begin: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});

export const zSentenceTokenRow = z.object({
  sentence_id: uuid,
  idx: z.number().int().nonnegative(),
  surface: z.string().min(1),
  reading_kata: z.string().nullable().optional(),
  dictionary_form: z.string().nullable().optional(),
  pos: z.string().nullable().optional(),
  pos_full: z.string().nullable().optional(),
  begin: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});

/**
 * Per-user, per-surface aggregated fluency state.
 * `history` is the full ResponseId[] log; `level` is the cached
 * WordReadingFluencyEnum computed by getWordReadingFluencyLevel(history).
 */
export const zFluencyRow = z.object({
  user_id: uuid,
  surface: z.string().min(1),
  history: z.array(zResponseId),
  level: zFluencyLevel,
  predicted_level: zFluencyLevel.nullable().optional(),
  last_engagement_session_id: uuid.nullable().optional(),
  updated_at: ts,
});

/**
 * Per-engagement event log row. One row per reader action that passes
 * shouldCreateRecord(). The `context` jsonb column mirrors LearningRecordContext.
 */
export const zLearningRecordRow = z.object({
  id: uuid,
  user_id: uuid,
  session_id: uuid,
  surface: z.string().min(1),
  response: zResponseId,
  action_id: zActionId,
  treatment_id: zTreatmentId,
  context: zLearningRecordContext,
  created_at: ts,
});

export const zVoteRow = z.object({
  user_id: uuid,
  reading_id: uuid,
  value: z.union([z.literal(1), z.literal(-1)]),
  created_at: ts,
});

export const zTopicClusterRow = z.object({
  id: z.number().int(),
  user_id: uuid,
  polarity: z.union([z.literal(1), z.literal(-1)]),
  centroid: z.array(z.number()).length(1024),
  size: z.number().int().nonnegative(),
});

export const zTokenFrequencyRow = z.object({
  surface: z.string().min(1),
  pos: z.string(),
  dictionary_form: z.string().nullable(),
  count: z.bigint(),
  rank: z.number().int().nonnegative(),
});

export const zGlossKind = z.enum(["sentence", "token"]);

/**
 * Cache for LLM-generated glosses/translations.
 * Composite PK (kind, text_hash, sentence_hash) — sentence_hash is zeroed
 * for kind='sentence' and set to sha256(containing_sentence) for kind='token'
 * so the same surface can gloss differently across sentences.
 */
export const zGlossCacheRow = z.object({
  kind: zGlossKind,
  text_hash: bytes,
  sentence_hash: bytes,
  text: z.string(),
  translation: z.string().nullable().optional(),
  gloss: z.string().nullable().optional(),
  romaji: z.string().nullable().optional(),
  reading_id: z.uuid().nullable().optional(),
  created_at: ts,
});

export const zTtsCacheRow = z.object({
  text_hash: bytes,
  blob_url: z.url(),
  voice: z.string(),
  created_at: ts,
});

export const zScriptForm = z.object({
  url: z.url(),
  attribution: z.string(),
});

export const zScriptForms = z.object({
  oracleBone: zScriptForm.optional(),
  chuSlip: zScriptForm.optional(),
  smallSeal: zScriptForm.optional(),
});

export const zEtymologyCacheRow = z.object({
  kanji: z.string().min(1),
  decomposition: z.string().nullable().optional(),
  semantic: z.string().nullable().optional(),
  phonetic: z.string().nullable().optional(),
  ancient_form: z.string().nullable().optional(),
  on_yomi_origin: z.string().nullable().optional(),
  gloss: z.string().nullable().optional(),
  script_forms: zScriptForms.nullable().optional(),
  source: z.string().min(1),
  model: z.string().nullable().optional(),
  wiktionary_revision: z.number().int().nullable().optional(),
  created_at: ts,
});

export const zAtprotoSessionRow = z.object({
  user_id: uuid,
  oauth_session: z.unknown(),
  updated_at: ts,
});

export const zReadingDismissalRow = z.object({
  user_id: uuid,
  reading_id: uuid,
  created_at: ts,
});

export const zInteractionKind = z.enum(["bookmark", "like"]);

export const zReadingInteractionRow = z.object({
  user_id: uuid,
  reading_id: uuid,
  kind: zInteractionKind,
  atproto_uri: z.string().nullable().optional(),
  created_at: ts,
});

export const zUserFollowsRow = z.object({
  user_id: uuid,
  follows_did: z.string().min(1),
  updated_at: ts,
});

// ---------- inferred TS types ----------

// Note: LearningPosition is exported from `@bindery/shared/contracts/learning` as the canonical
// hand-written type; zLearningPosition above mirrors it for runtime validation and is annotated
// against it. LearningRecordContext and LearningRecordEntry are inferred from the zod schemas
// here rather than hand-written anywhere, because these two shapes are the on-disk ones and no
// hand-written twin should be free to drift from the column they describe.

// VerbKind / AdjectiveKind are exported from ./morphology — see comment
// at the top of schemas.ts on canonical-vs-mirror types.

export type LearningRecordContext = z.infer<typeof zLearningRecordContext>;
export type LearningRecordEntry = z.infer<typeof zLearningRecordEntry>;

export type UserRow = z.infer<typeof zUserRow>;
export type UsageEventKind = z.infer<typeof zUsageEventKind>;
export type UsageEventRow = z.infer<typeof zUsageEventRow>;
export type ReadingSource = z.infer<typeof zReadingSource>;
export type ReadingRow = z.infer<typeof zReadingRow>;
export type SentenceRow = z.infer<typeof zSentenceRow>;
export type SentenceTokenRow = z.infer<typeof zSentenceTokenRow>;
export type FluencyRow = z.infer<typeof zFluencyRow>;
export type LearningRecordRow = z.infer<typeof zLearningRecordRow>;
export type VoteRow = z.infer<typeof zVoteRow>;
export type TopicClusterRow = z.infer<typeof zTopicClusterRow>;
export type TokenFrequencyRow = z.infer<typeof zTokenFrequencyRow>;
export type GlossKind = z.infer<typeof zGlossKind>;
export type GlossCacheRow = z.infer<typeof zGlossCacheRow>;
export type TtsCacheRow = z.infer<typeof zTtsCacheRow>;
export type EtymologyCacheRow = z.infer<typeof zEtymologyCacheRow>;
export type AtprotoSessionRow = z.infer<typeof zAtprotoSessionRow>;
export type ReadingDismissalRow = z.infer<typeof zReadingDismissalRow>;
export type InteractionKind = z.infer<typeof zInteractionKind>;
export type ReadingInteractionRow = z.infer<typeof zReadingInteractionRow>;
export type UserFollowsRow = z.infer<typeof zUserFollowsRow>;
export type UserSelfCentroidRow = z.infer<typeof zUserSelfCentroidRow>;
